import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  Package,
  ShoppingCart,
  History,
  LogOut,
  Menu,
  X,
  Home,
  FileText,
  Bell,
  Truck,
  Boxes
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface ClientLayoutProps {
  children: React.ReactNode;
}

const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isCompany = user?.clientType === 'empresa';

  const menuItems = [
    {
      path: '/cliente',
      icon: <Home className="h-5 w-5" />,
      label: 'Inicio',
      exact: true
    },
    ...(!isCompany ? [{
      path: '/cliente/productos',
      icon: <Package className="h-5 w-5" />,
      label: 'Catálogo'
    },
    {
      path: '/cliente/cotizaciones',
      icon: <FileText className="h-5 w-5" />,
      label: 'Cotizaciones'
    }] : []),
    {
      path: '/cliente/pedidos',
      icon: <ShoppingCart className="h-5 w-5" />,
      label: 'Mis Pedidos'
    },
    ...(isCompany ? [{
      path: '/cliente/stock',
      icon: <Boxes className="h-5 w-5" />,
      label: 'Mi Stock'
    }] : []),
    {
      path: '/cliente/remitos',
      icon: <Truck className="h-5 w-5" />,
      label: 'Remitos'
    },
    ...(!isCompany ? [{
      path: '/cliente/historial',
      icon: <History className="h-5 w-5" />,
      label: 'Historial'
    },
    {
      path: '/cliente/notificaciones',
      icon: <Bell className="h-5 w-5" />,
      label: 'Notificaciones'
    }] : []),
    {
      path: '/cliente/perfil',
      icon: <User className="h-5 w-5" />,
      label: 'Mi Perfil'
    }
  ];

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Sin esto la pestaña queda con el título de la pantalla anterior (por ejemplo "Iniciar Sesión").
  const currentSectionLabel = menuItems.find((item) => item.path !== '/cliente' && isActive(item.path, item.exact))?.label || 'Inicio';
  useEffect(() => {
    document.title = `${currentSectionLabel} - Plásticos Polcen`;
  }, [currentSectionLabel]);

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:flex lg:flex-col lg:h-screen ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <img
              src="/images/logo.jpg"
              alt="Plásticos Polcen"
              className="h-8 w-8 rounded-lg object-cover"
            />
            <span className="ml-3 text-lg font-semibold text-gray-900">
              Mi Cuenta
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User info */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">Cliente</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 flex-1 overflow-y-auto pb-4">
          <div className="space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {item.icon}
                <span className="ml-3">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Logout button */}
        <div className="p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="ml-3">Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Bienvenido, <span className="font-medium text-gray-900">{user?.name}</span>
              </div>

            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default ClientLayout;
